import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const DEMO_CREDENTIALS = [
  { code: 'HSG-CHEN-2024', name: 'James Chen', property: 'The Meridian Penthouse' },
  { code: 'HSG-WESTON-2024', name: 'Victoria Weston', property: 'Harbour View Estate' },
  { code: 'HSG-LAURENT-2024', name: 'Marc Laurent', property: 'The Residences at Park Lane' },
];

export default function PortalLogin() {
  const navigate = useNavigate();
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    setTimeout(() => {
      const match = DEMO_CREDENTIALS.find(
        (c) => c.code.toLowerCase() === accessCode.trim().toLowerCase()
      );
      if (match) {
        sessionStorage.setItem('hsg_client', JSON.stringify(match));
        navigate('/portal/dashboard');
      } else {
        setError('Access code not recognised. Please contact your relationship manager.');
      }
      setLoading(false);
    }, 900);
  };

  return (
    <div className="min-h-screen w-full flex">
      {/* Left — Image Panel */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/0bf420e4e7a24f98bad88c5b9d2a7684.jpg"
          alt="Higher Standard Global Properties"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col justify-between p-14">
          <a href="/" className="cursor-pointer">
            <img
              src="https://static.readdy.ai/image/c81a63152c5e17d7d9cf1c59f52b9bbd/5d23d4ffe74dcae8c00b4613d299989b.png"
              alt="Higher Standard Global"
              className="h-12 w-auto object-contain"
            />
          </a>
          <div>
            <div className="inline-flex items-center border border-white/30 rounded-full px-4 py-1.5 mb-5">
              <span className="text-white/70 text-xs uppercase tracking-[0.2em]">Client Portal</span>
            </div>
            <h2 className="text-white text-3xl font-light leading-snug max-w-xs" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Your properties, managed with precision.
            </h2>
            <p className="text-white/60 text-sm font-light mt-4 max-w-xs leading-relaxed">
              Access real-time reports, maintenance updates, and lease documents — all in one secure place.
            </p>
          </div>
        </div>
      </div>

      {/* Right — Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center bg-[#FAFAF8] px-8 md:px-16 py-16">
        {/* Mobile Logo */}
        <div className="lg:hidden mb-10">
          <a href="/">
            <img
              src="https://static.readdy.ai/image/c81a63152c5e17d7d9cf1c59f52b9bbd/5d23d4ffe74dcae8c00b4613d299989b.png"
              alt="Higher Standard Global"
              className="h-10 w-auto object-contain"
            />
          </a>
        </div>

        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-10">
            <div className="inline-flex items-center border border-[#2C2C2C]/20 rounded-full px-4 py-1.5 mb-5">
              <span className="text-[#8B7355] text-xs uppercase tracking-[0.2em] font-medium">Secure Access</span>
            </div>
            <h1 className="text-3xl font-semibold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Client Portal
            </h1>
            <p className="text-[#6B6B6B] text-sm font-light leading-relaxed">
              Enter your unique access code provided by your relationship manager to access your property dashboard.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <label className="text-xs uppercase tracking-[0.15em] text-[#5A5A5A] font-medium">
                Access Code
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-key-2-line text-[#8B7355] text-base"></i>
                </div>
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => { setAccessCode(e.target.value); setError(''); }}
                  placeholder="e.g. HSG-XXXXX-2024"
                  className="w-full pl-11 pr-4 py-3.5 bg-white border border-[#E0DDD9] rounded-md text-sm text-[#1A1A1A] placeholder-[#AEAAA4] focus:outline-none focus:border-[#8B7355] transition-colors"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              {error && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1.5">
                  <i className="ri-error-warning-line"></i>
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !accessCode.trim()}
              className="w-full py-3.5 bg-[#2C2C2C] hover:bg-[#1A1A1A] disabled:bg-[#AEAAA4] text-white text-sm font-medium tracking-wide rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Verifying...
                </>
              ) : (
                <>
                  <i className="ri-shield-check-line"></i>
                  Access My Portal
                </>
              )}
            </button>
          </form>

          {/* Help */}
          <div className="mt-10 pt-8 border-t border-[#E8E6E3]">
            <p className="text-xs text-[#9A9A9A] text-center leading-relaxed">
              Don&apos;t have an access code?{' '}
              <a href="/#contact" className="text-[#8B7355] hover:text-[#6B5A42] transition-colors cursor-pointer">
                Contact your relationship manager
              </a>
            </p>
            <p className="text-xs text-[#AEAAA4] text-center mt-3">
              This portal is exclusively for referred clients of Higher Standard Global.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
